/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useAuth0} from '@auth0/auth0-react';
import {TabContext, TabList, TabPanel} from '@mui/lab';
import {Box, Card, CardContent, Stack, Tab, TextField} from '@mui/material';
import CardHeader from '@mui/material/CardHeader';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {FC, SyntheticEvent, useContext, useEffect, useState} from 'react';
import {DevMUITheme} from '../components/DevMUITheme';
import {PageContainer} from '../components/PageContainer';
import type {FeatureFlagSet} from '../flags';
import {RuntimeConfigContext} from '../providers/RuntimeContextProvider';
import {useUserStore} from '../stores/userStore';

export const DevPage: FC = () => {
  const {user: auth0User, getAccessTokenSilently} = useAuth0();
  const [token, setToken] = useState<string | undefined>();
  const [value, setValue] = useState('user');
  const storeUser = useUserStore((state) => state);
  const allFlags = useFlags<FeatureFlagSet>();
  const runtimeContext = useContext(RuntimeConfigContext);

  useEffect(() => {
    if (auth0User) {
      void getAccessTokenSilently().then(setToken);
    }
  }, [auth0User]);

  const handleChange = (_event: SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  return (
    <PageContainer title={'Developer'}>
      <Stack>
        <TabContext value={value}>
          <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
            <TabList onChange={handleChange}>
              <Tab label={'User'} value={'user'} />
              <Tab label={'Flags'} value={'flags'} />
              <Tab label={'Config'} value={'config'} />
              <Tab label={'MUI'} value={'mui'} />
            </TabList>
          </Box>
          <TabPanel value={'user'} sx={{px: 0}}>
            <Stack spacing={3}>
              <Card>
                <CardHeader title={'Store User'} />
                <CardContent>
                  <TextField fullWidth multiline value={JSON.stringify(storeUser, null, 2)} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader title={'Auth0 User'} />
                <CardContent>
                  <TextField fullWidth multiline value={JSON.stringify(auth0User, null, 2)} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader title={'Bearer Token'} />
                <CardContent>
                  <TextField fullWidth multiline value={token} sx={{w: '100%'}} />
                </CardContent>
              </Card>
            </Stack>
          </TabPanel>
          <TabPanel value={'flags'} sx={{px: 0}}>
            <Card>
              <CardHeader title={'Feature Flags'} />
              <CardContent>
                <TextField fullWidth multiline value={JSON.stringify(allFlags, null, 2)} sx={{w: '100%'}} />
              </CardContent>
            </Card>
          </TabPanel>
          <TabPanel value={'config'} sx={{px: 0}}>
            <Card>
              <CardHeader title={'Runtime Config'} />
              <CardContent>
                <TextField fullWidth multiline value={JSON.stringify(runtimeContext, null, 2)} sx={{w: '100%'}} />
              </CardContent>
            </Card>
          </TabPanel>
          <TabPanel value={'mui'} sx={{px: 0}}>
            <DevMUITheme />
          </TabPanel>
        </TabContext>
      </Stack>
    </PageContainer>
  );
};
